import React from "react";
import { withPrefix } from "gatsby";
import get from "lodash/get";
import compact from "lodash/compact";
import startsWith from "lodash/startsWith";
import map from "lodash/map";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import filter from "lodash/filter";
import uniqBy from "lodash/uniqBy";
import {
  getAwHeadScript,
  getGtmHeadScript,
  initGtagScript,
  gtagDataLayerInitScript,
} from "../utils/gtm";
import { getAppMediaSrc, mediaTypes } from "../utils/media";
import { isNewsfeedTemplateLayout } from "../logic/templates/isNewsfeedTemplateLayout";
import { getParams } from "../utils/location";

const BUTT_HUTT_BUSINESS_ID = '685e989bbfb71782cba19af8';

/**
 * Head component for Gatsby Head API
 * Returns raw JSX for document head (no Helmet wrapper needed)
 */
const Head = (props) => {
  const { app = {}, T, location = {}, appStyles = {}, business = {}, branches = [], pageContext = {} } = props;
  const { trackings = [], links = {} } = business;
  const usingNewsfeedTemplateLayout = isNewsfeedTemplateLayout(appStyles);
  const isFullscreen =
    !(
      app.mobileAuthViewOpen ||
      app.authModalOpen ||
      usingNewsfeedTemplateLayout
    ) && location.pathname === "/";

  const height = isFullscreen ? "100%" : "auto";
  const overflow = isFullscreen ? "hidden" : "auto";
  const position = isFullscreen ? "fixed" : "relative";
  const letterSpacing = appStyles.letterSpacing;
  const background = appStyles.backgroundImage
    ? `url(${withPrefix(appStyles.backgroundImage)})`
    : appStyles.background;
  const fontSize = appStyles.baseFontSize || 16;
  
  // Get language from appStyles
  const language = appStyles.defaultLanguage || "en-us";
  const languageCode = language.split("-")[0]; // Get 'en' from 'en-us'

  const HTMLStyleAttribute = {
    height,
    fontSize,
    overflow,
    position,
    ...(letterSpacing && { letterSpacing }),
    ...(background && { background }),
  };

  const googleFontLink = toGoogleFontLink(appStyles.googleFonts);
  const facebookMetaTagId = getFacebookMetaTagId(trackings);
  const trackingIds = map(
    uniqBy(filter(trackings, { type: "gtm" }), "trackingId"),
    "trackingId",
  );
  const awIds = filter(trackingIds, (id) => startsWith(id, "AW"));
  const gtmIds = filter(trackingIds, (id) => startsWith(id, "GTM"));

  const appStoreId = get(links, "appStoreAppId");

  const { branchId, branchName } = getParams(location);

  const isBranchSelected = Boolean(branchId && branchName);

  const branch = branches.find(({id}) => id === branchId);
  
  const businessNameContent =
    isBranchSelected && branch
      ? `{{business_name}} | ${branchName}`
      : "{{business_name}}";

  // Get new-website SEO configuration
  const websiteSeo = get(appStyles, "newWebsite.seo", {}) || {};
  const currentPageIndex = Number.isInteger(pageContext?.currentPageIndex)
    ? pageContext.currentPageIndex
    : null;
  const pageSeo =
    currentPageIndex !== null
      ? get(appStyles, ["newWebsite", "pages", currentPageIndex, "seo"], {})
      : null;
  const resolveSeoValue = (field) => {
    const pageValue = get(pageSeo, field);
    if (pageValue) {
      return pageValue;
    }
    return get(websiteSeo, field);
  };

  const defaultTitle = branchName
    ? T("{{business_title}}", { locationName: branchName })
    : T(`${businessNameContent} - Just Order`);

  const title = resolveSeoValue("title") || defaultTitle;
  const description = resolveSeoValue("metaDescription") || null;
  const openGraphImage =
    get(pageSeo, "openGraphImageUrl") ||
    get(websiteSeo, "openGraphImageUrl") ||
    getAppMediaSrc(mediaTypes.logo, appStyles);

  return (
    <>
      <html lang={languageCode} style={HTMLStyleAttribute} />
      <title>{title}</title>
      
      {/* Basic Meta Tags */}
      {description && <meta name="description" content={description} />}
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      {appStoreId && <meta name="apple-itunes-app" content={`app-id=${appStoreId}`} />}
      {facebookMetaTagId && <meta name="facebook-domain-verification" content={facebookMetaTagId} />}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      <meta name="author" content={T("{{business_name}}")} />
      <meta name="language" content={languageCode} />
      
      {/* Open Graph Tags */}
      <meta property="og:site_name" content={T(businessNameContent)} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      {openGraphImage && <meta property="og:image" content={openGraphImage} />}
      {openGraphImage && <meta property="og:image:width" content="1200" />}
      {openGraphImage && <meta property="og:image:height" content="630" />}
      <meta property="og:url" content={links.webApp} />
      <meta property="og:locale" content={language.replace("-", "_")} />
      
      {/* Twitter Cards */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:site" content={links.webApp} />
      {title && <meta property="twitter:title" content={title} />}
      {description && <meta property="twitter:description" content={description} />}
      <meta property="twitter:image" content={openGraphImage} />
      
      {/* Business-specific verifications */}
      {business.id === BUTT_HUTT_BUSINESS_ID && (
        <meta name="ahrefs-site-verification" content="6c38399b697dd54e18e655552cfe23ca7e0e46379396792635aa2a6bcf1b9523" />
      )}
      {business.id === BUTT_HUTT_BUSINESS_ID && (
        <meta name="google-site-verification" content="C1riNv7sVmPOgeC1CglNC7elPNy81rCkhTUoV-yb7cU" />
      )}
      
      {/* Link Tags */}
      <link rel="icon" sizes="64x64" type="image/png" href={getAppMediaSrc(mediaTypes.appIcon, appStyles)} />
      <link rel="icon" sizes="48x48" type="image/png" href={getAppMediaSrc(mediaTypes.appIcon, appStyles, 48)} />
      {googleFontLink && <link rel="stylesheet" href={googleFontLink} />}
      <link rel="canonical" href={links.webApp} />
      
      {/* Tracking Scripts */}
      {map(awIds, (awId, index) => (
        <script key={`aw-${index}`} async src={getAwHeadScript(awId)} />
      ))}
      <script>{gtagDataLayerInitScript}</script>
      <script>{"dataLayer=window.dataLayer || [];"}</script>
      {map(gtmIds, (gtmId, index) => (
        <script key={`gtm-${index}`}>{getGtmHeadScript(gtmId)}</script>
      ))}
      {!isEmpty(awIds) && <script>{initGtagScript(awIds)}</script>}
      
      {/* Schema Markup */}
      {generateSchemaMarkup(business, branch)}
      
      {/* Conditional Styles */}
      {get(appStyles, "hideRecaptchaBadge") && (
        <style type="text/css">
          {`.grecaptcha-badge { 
            visibility: hidden;
          }`}
        </style>
      )}
      
      {/* Business-specific Analytics */}
      {business.id === BUTT_HUTT_BUSINESS_ID && (
        <script async src="https://analytics.ahrefs.com/analytics.js" data-key="UO75s1L4qeSCM1fW50s7rA" />
      )}
    </>
  );
};

export default Head;

// Helper Functions

const googleFontBaseUrl = "https://fonts.googleapis.com/css2";

const toGoogleFontLink = (googleFonts) => {
  if (isEmpty(googleFonts)) {
    return null;
  }
  const googleFontQueryString = map(googleFonts, (googleFont) => {
    const [family, weights = ""] = googleFont.split(":");
    const hasItalics = includes(weights, "i");
    const hasWeights = Boolean(weights.length);
    const fontQS = `family=${family}${hasItalics || hasWeights ? ":" : ""}${
      hasItalics ? "ital" : ""
    }${hasItalics && hasWeights ? "," : ""}${hasWeights ? "wght@" : ""}${map(
      weights.split(","),
      (weight) => {
        return `${
          hasItalics ? (includes(weight, "i") ? "1," : "0,") : ""
        }${parseInt(weight, 10)}`;
      },
    ).join(";")}`;
    return fontQS;
  }).join("&");
  return `${googleFontBaseUrl}?${googleFontQueryString}&display=swap`;
};

const getFacebookMetaTagId = (trackings) => {
  const fbTracking = find(trackings, { type: "facebook" });
  if (fbTracking) {
    const metaTagId = get(
      find(fbTracking.events, {
        type: "MetaTagVerification",
      }),
      "params.metaTag",
    );
    return metaTagId;
  }
};

const generateSchemaMarkup = (business, branch) => {
  const businessNameContent = branch ? `${business.name} | ${branch.name}` : business.name;

  // Base schema for Restaurant
  const restaurantSchema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": business?.links?.webApp,
    "name": businessNameContent,
    "url": business?.links?.webApp,
    "telephone": get(branch, "phoneNumber") || get(business, "phone"),
    ...(branch && {"address": {
      "@type": "PostalAddress",
      "streetAddress": get(branch, "deliveryAddress.street"),
      "addressLocality": get(branch, "deliveryAddress.city"),
      "addressRegion": get(branch, "deliveryAddress.state"),
      "postalCode": get(branch, "deliveryAddress.zipCode"),
      "addressCountry": "USA"
    }}),
    ...(branch && branch.openHours ? { 
      "openingHours": [branch.openHours], 
      "menu": `${business?.links?.webApp}order/?branchId=${branch.id}&branchName=${branch.name}&search=/` 
    } : {}),
    "servesCuisine": get(business, "cuisineType"),
    "priceRange": get(business, "priceRange"),
    "image": get(branch, "imageKey") || get(business, "desktopBackgroundImage.url"),
    "acceptsReservations": "False",
    "hasMenu": `${business?.links?.webApp}menu`,
  };

  // Add geo coordinates if available
  if (get(branch, "latitude") && get(branch, "longitude")) {
    restaurantSchema.geo = {
      "@type": "GeoCoordinates",
      "latitude": branch.latitude,
      "longitude": branch.longitude
    };
  }

  // Add serving options
  if (get(business, "servingOptions")) {
    restaurantSchema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      "name": "Food Menu",
      "itemListElement": []
    };
  }

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${business?.links?.webApp}#website`,
    "url": business?.links?.webApp,
    "name": business.name,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${business?.links?.webApp}order/?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${business?.links?.webApp}#organization`,
    "name": business.name,
    "url": business?.links?.webApp,
    "logo": get(business, "appStyles.appIcon"),
    "sameAs": compact([
      get(business, "links.facebook"),
      get(business, "links.instagram"),
      get(business, "links.twitter")
    ])
  };

  // Combine schemas using @graph
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": compact([
      restaurantSchema,
      websiteSchema,
      organizationSchema
    ])
  };

  return (
    <script type="application/ld+json">
      {JSON.stringify(combinedSchema)}
    </script>
  );
};
